import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Award, Users, Clock, Star, ChevronRight } from "lucide-react";

export default function FoundingCircle() {
  const { user, checkAuth } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get("/pilot/founding/status");
      setStatus(res.data);
    } catch (err) {
      toast.error("Failed to load founding status");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      const res = await api.post("/pilot/founding/join");
      toast.success(res.data.message);
      await fetchStatus();
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not join");
    } finally {
      setJoining(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  const pctClaimed = status ? Math.round((status.claimed / status.total_slots) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="founding-circle-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 p-6 sm:p-10 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-600/20 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-6 h-6 text-amber-400" />
            <span className="text-amber-400 font-heading uppercase tracking-wider text-sm">Exclusive Program</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-3">Founding Players Circle</h1>
          <p className="text-emerald-100 text-base max-w-xl">
            Be among the first {status?.total_slots?.toLocaleString()} players to shape the future of SetPlay.
            Founding members get exclusive perks, priority access, and a permanent badge.
          </p>
        </div>
      </div>

      {/* Counter */}
      <Card data-testid="founding-counter">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Founding Slots</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-heading font-bold text-primary">{status?.remaining?.toLocaleString()}</span>
                <span className="text-muted-foreground">of {status?.total_slots?.toLocaleString()} remaining</span>
              </div>
            </div>
            <div className="w-full sm:w-64">
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${pctClaimed}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1 text-right">{pctClaimed}% claimed</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status / Join */}
      {status?.is_founder ? (
        <Card className="border-amber-400 border-2" data-testid="founder-badge-card">
          <CardContent className="p-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
              <Award className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="font-heading text-2xl mb-2">You're Founding Member #{status.founding_number}</h2>
            <p className="text-muted-foreground">Your founding badge is permanently on your profile. Enjoy priority booking and exclusive perks!</p>
            <Badge className="mt-3 bg-amber-500 text-white">Founding Player #{status.founding_number}</Badge>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="join-founding-card">
          <CardContent className="p-6 text-center">
            <h2 className="font-heading text-xl mb-3">Ready to join?</h2>
            <p className="text-muted-foreground mb-4">Complete at least 1 booking to become eligible for the Founding Players Circle.</p>
            <Button onClick={handleJoin} disabled={joining} size="lg" className="rounded-full px-8" data-testid="join-founding-btn">
              {joining ? "Joining..." : "Join the Founding Circle"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Benefits */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {(status?.benefits || []).map((b, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                {i === 0 && <Award className="w-4 h-4 text-primary" />}
                {i === 1 && <Clock className="w-4 h-4 text-primary" />}
                {i === 2 && <Star className="w-4 h-4 text-primary" />}
                {i === 3 && <ChevronRight className="w-4 h-4 text-primary" />}
                {i === 4 && <Users className="w-4 h-4 text-primary" />}
              </div>
              <span className="text-sm">{b}</span>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
